// Google Identity Services, ID token flow (https://developers.google.com/identity/gsi/web/guides/overview)
// — captain SSO. Google renders its own "Sign in with Google" button and hands back a signed ID token
// (`credential`); the backend verifies it against Google's keys and signs the captain in. There is no code
// exchange and no client secret anywhere on the client. Popup mode: captain runs inside Twilio Flex's iframe
// and the Zendesk app inside Zendesk's, where a redirect can't work.

const GOOGLE_WORKSPACE_DOMAIN = 'resupplyme.com';
const GSI_CLIENT_SRC = 'https://accounts.google.com/gsi/client';

declare const google: {
  accounts: {
    id: {
      initialize(config: {
        client_id: string;
        callback: (response: { credential?: string }) => void;
        ux_mode: 'popup';
        hd?: string;
        auto_select?: boolean;
        cancel_on_tap_outside?: boolean;
      }): void;
      renderButton(
        parent: HTMLElement,
        options: {
          type: 'standard';
          theme: 'outline';
          size: 'large';
          text: 'signin_with';
          shape: 'pill';
          logo_alignment: 'left';
          width?: number;
        },
      ): void;
    };
  };
};

let gsiClient: Promise<void> | undefined;

export function loadGoogleSignIn(): Promise<void> {
  gsiClient ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = GSI_CLIENT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      script.remove();
      gsiClient = undefined;
      reject(new Error('Google sign-in failed to load'));
    };
    document.head.appendChild(script);
  });
  return gsiClient;
}

// Call only after loadGoogleSignIn() resolved. Draws Google's button into `host` (Google's branding, pill,
// "Sign in with Google"); onIdToken gets the signed ID token once the user picked an account.
export function renderGoogleSignInButton(host: HTMLElement, clientId: string, onIdToken: (idToken: string) => void): void {
  google.accounts.id.initialize({
    client_id: clientId,
    ux_mode: 'popup',
    // the account chooser offers Workspace accounts only; the API still refuses anything that isn't a captain
    hd: GOOGLE_WORKSPACE_DOMAIN,
    auto_select: false,
    cancel_on_tap_outside: true,
    callback: (response) => {
      if (response.credential) onIdToken(response.credential);
    },
  });
  google.accounts.id.renderButton(host, {
    type: 'standard',
    theme: 'outline',
    size: 'large',
    text: 'signin_with',
    shape: 'pill',
    logo_alignment: 'left',
    width: Math.min(host.clientWidth || 320, 400),
  });
}
